import { useMemo, useState, type FormEvent } from 'react'
import {
  useBriefingTopics,
  useFaqTopics,
  useCreateBriefingTopic,
  useUpdateBriefingTopic,
  useDeleteBriefingTopic
} from '../hooks/useBriefings'
import {
  ModalShell, Field, inputClass,
  CancelButton, PrimaryButton, DangerButton, ServerError
} from '../components/modal-form'
import { Icon } from '../components/Icon'
import { useMe } from '../hooks/useMe'
import type { BriefingTopic, BriefingTopicBody } from '../types'

// AdminBriefingTopics — admin-only page for managing the org-level briefing
// topics catalogue (spec 05 §library) AND the guest FAQ library (mig 067).
// Two sections: operational staff briefings (acknowledged pre-shift), and the
// guest FAQ library the Product Briefing "Generate FAQ" button draws on.

type EditTarget = { mode: 'topic' | 'faq'; initial: BriefingTopic | null }

export function AdminBriefingTopics() {
  const { data: me } = useMe()
  const { data: topics, isLoading, error } = useBriefingTopics()
  const { data: faqs } = useFaqTopics()
  const [editing, setEditing] = useState<EditTarget | null>(null)

  // Group operational topics by category for visual scanability.
  const grouped = useMemo(() => {
    const map = new Map<string, BriefingTopic[]>()
    for (const t of topics ?? []) {
      if (!t.isActive) continue
      const arr = map.get(t.category) ?? []
      arr.push(t)
      map.set(t.category, arr)
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]))
  }, [topics])

  // Group FAQs by scope ('all' first, then event types alphabetically).
  const faqGrouped = useMemo(() => {
    const map = new Map<string, BriefingTopic[]>()
    for (const t of faqs ?? []) {
      if (!t.isActive) continue
      const scope = t.faqScope ?? 'all'
      const arr = map.get(scope) ?? []
      arr.push(t)
      map.set(scope, arr)
    }
    return Array.from(map.entries()).sort((a, b) =>
      a[0] === 'all' ? -1 : b[0] === 'all' ? 1 : a[0].localeCompare(b[0]))
  }, [faqs])

  if (me && !me.isAdmin) {
    return (
      <div className="space-y-5">
        <div>
          <h1 className="text-3xl mb-1">Briefing topics</h1>
          <p className="text-ink-soft">Admin only.</p>
        </div>
        <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
          Editing briefing topics requires admin permissions.
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl mb-1">Briefing topics</h1>
          <p className="text-ink-soft">
            Pre-shift safety + welcome briefings volunteers acknowledge before
            their first duty on an event. Adding a topic here makes it
            available for event managers to attach on the Briefings tab.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing({ mode: 'topic', initial: null })}
          className="px-4 py-2 text-sm font-semibold rounded bg-green text-parchment hover:bg-green/90 transition-colors"
        >
          + New topic
        </button>
      </div>

      {isLoading && (
        <div className="text-ink-soft text-sm font-mono">Loading briefing topics…</div>
      )}

      {error && (
        <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
          <div className="font-semibold mb-1">Could not load briefing topics</div>
          <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
        </div>
      )}

      {topics && topics.filter((t) => t.isActive).length === 0 && (
        <div className="bg-white border border-line rounded p-10 text-center">
          <div
            className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
            style={{ background: '#e0e7ec', color: '#3d5a6c' }}
          >
            <Icon name="info" size={22} />
          </div>
          <h3 className="font-display text-lg mb-1">No briefing topics yet</h3>
          <p className="text-sm text-ink-soft mb-4 max-w-md mx-auto">
            Add a topic and event managers can attach it to a Briefings tab so
            volunteers acknowledge it before their shift.
          </p>
          <button
            type="button"
            onClick={() => setEditing({ mode: 'topic', initial: null })}
            className="px-4 py-2 rounded text-sm font-semibold bg-green text-parchment hover:bg-green/90 transition-colors"
          >
            + Add the first topic
          </button>
        </div>
      )}

      {grouped.map(([category, entries]) => (
        <section key={category} className="bg-white border border-line rounded overflow-hidden">
          <header className="px-4 py-2.5 border-b border-line bg-parchment/60 flex items-baseline justify-between">
            <h2 className="text-sm font-semibold">{category}</h2>
            <span className="text-xs text-ink-soft font-mono">
              {entries.length} topic{entries.length === 1 ? '' : 's'}
            </span>
          </header>
          <div className="divide-y divide-line">
            {entries.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setEditing({ mode: 'topic', initial: t })}
                className="w-full text-left px-4 py-3 flex items-start gap-4 hover:bg-parchment/40 transition-colors"
              >
                <Icon name="info" size={16} className="text-slate flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{t.title}</div>
                  {t.requiredForRoles && t.requiredForRoles.length > 0 && (
                    <div className="text-xs text-ink-soft mt-0.5 truncate">
                      Required for: {t.requiredForRoles.join(', ')}
                    </div>
                  )}
                </div>
                <div className="text-xs text-ink-soft font-mono whitespace-nowrap flex items-center gap-2">
                  <span>{t.durationMinutes} min</span>
                  <span className="px-1.5 py-0.5 rounded bg-parchment text-[10px]">v{t.version}</span>
                </div>
              </button>
            ))}
          </div>
        </section>
      ))}

      {/* ---- Guest FAQ library (mig 067) ------------------------------------ */}
      <div className="flex items-end justify-between gap-4 flex-wrap pt-2">
        <div>
          <h2 className="text-2xl mb-1">Guest FAQ library</h2>
          <p className="text-ink-soft text-sm max-w-2xl">
            Standard guest questions &amp; answers. The Product Briefing "Generate FAQ"
            button assembles the FAQs that apply to an event and fills in live
            details. Use tokens like <code className="font-mono text-xs">{'{{ticketPrices}}'}</code>,{' '}
            <code className="font-mono text-xs">{'{{departures}}'}</code> and{' '}
            <code className="font-mono text-xs">{'{{dates}}'}</code> in an answer — they're
            replaced with each event's real data.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing({ mode: 'faq', initial: null })}
          className="px-4 py-2 text-sm font-semibold rounded bg-green text-parchment hover:bg-green/90 transition-colors"
        >
          + New FAQ
        </button>
      </div>

      {faqGrouped.map(([scope, entries]) => (
        <section key={scope} className="bg-white border border-line rounded overflow-hidden">
          <header className="px-4 py-2.5 border-b border-line bg-parchment/60 flex items-baseline justify-between">
            <h2 className="text-sm font-semibold">{scope === 'all' ? 'All events' : scope}</h2>
            <span className="text-xs text-ink-soft font-mono">
              {entries.length} FAQ{entries.length === 1 ? '' : 's'}
            </span>
          </header>
          <div className="divide-y divide-line">
            {entries.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setEditing({ mode: 'faq', initial: t })}
                className="w-full text-left px-4 py-3 flex items-start gap-4 hover:bg-parchment/40 transition-colors"
              >
                <Icon name="info" size={16} className="text-slate flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{t.title}</div>
                  <div className="text-xs text-ink-soft mt-0.5 truncate">{t.bodyMarkdown.replace(/[#*_>]/g, '').slice(0, 120)}</div>
                </div>
                <span className="text-xs text-ink-soft font-mono px-1.5 py-0.5 rounded bg-parchment text-[10px] whitespace-nowrap">
                  {scope === 'all' ? 'all events' : scope}
                </span>
              </button>
            ))}
          </div>
        </section>
      ))}

      {editing && (
        <BriefingTopicEditor
          faqMode={editing.mode === 'faq'}
          initial={editing.initial}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}

// ---- Editor modal --------------------------------------------------------

function BriefingTopicEditor({
  faqMode, initial, onClose
}: {
  faqMode: boolean
  initial: BriefingTopic | null
  onClose: () => void
}) {
  const isEditing = initial != null
  const createMut = useCreateBriefingTopic()
  const updateMut = useUpdateBriefingTopic(initial?.id ?? -1)
  const deleteMut = useDeleteBriefingTopic()

  const [title, setTitle]             = useState(initial?.title ?? '')
  const [category, setCategory]       = useState(initial?.category ?? (faqMode ? 'FAQ' : ''))
  const [duration, setDuration]       = useState(String(initial?.durationMinutes ?? 5))
  const [body, setBody]               = useState(initial?.bodyMarkdown ?? '')
  const [rolesText, setRolesText]     = useState(initial?.requiredForRoles?.join(', ') ?? '')
  const [faqScope, setFaqScope]       = useState(initial?.faqScope ?? 'all')

  const trimmedTitle = title.trim()
  const trimmedCategory = faqMode ? 'FAQ' : category.trim()
  const durationNum = faqMode ? 0 : Number(duration)

  const fieldErrors: { title?: string; category?: string; duration?: string; body?: string; scope?: string } = {}
  if (trimmedTitle.length === 0) fieldErrors.title = faqMode ? 'Question is required' : 'Title is required'
  else if (trimmedTitle.length > 200) fieldErrors.title = 'Must be 200 characters or fewer'
  if (!faqMode) {
    if (trimmedCategory.length === 0) fieldErrors.category = 'Category is required'
    else if (trimmedCategory.length > 80) fieldErrors.category = 'Category must be 80 characters or fewer'
    if (!Number.isInteger(durationNum) || durationNum < 0 || durationNum > 240) {
      fieldErrors.duration = 'Duration must be a whole number 0..240'
    }
  } else {
    if (faqScope.trim().length === 0) fieldErrors.scope = 'Scope is required'
    else if (faqScope.trim().length > 60) fieldErrors.scope = 'Scope must be 60 characters or fewer'
  }
  if (body.trim().length === 0) fieldErrors.body = faqMode ? 'Answer is required' : 'Briefing body is required'

  const isValid = Object.keys(fieldErrors).length === 0
  const saving = createMut.isPending || updateMut.isPending
  const deleting = deleteMut.isPending
  const serverError = (createMut.error ?? updateMut.error ?? deleteMut.error) as Error | null

  function parseRoles(text: string): string[] | null {
    const roles = text.split(',').map((r) => r.trim()).filter((r) => r.length > 0)
    return roles.length > 0 ? roles : null
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!isValid || saving) return
    const submission: BriefingTopicBody = {
      title: trimmedTitle,
      category: trimmedCategory,
      durationMinutes: durationNum,
      bodyMarkdown: body,
      requiredForRoles: faqMode ? null : parseRoles(rolesText),
      isFaq: faqMode,
      faqScope: faqMode ? faqScope.trim() : null
    }
    if (isEditing && initial) {
      updateMut.mutate(submission, { onSuccess: () => onClose() })
    } else {
      createMut.mutate(submission, { onSuccess: () => onClose() })
    }
  }

  function handleDelete() {
    if (!initial) return
    const ok = window.confirm(
      faqMode
        ? `Remove the FAQ "${initial.title}"? It won't appear in generated briefings any more.`
        : `Deactivate "${initial.title}"? Existing event briefings keep working; future events won't see it in the picker.`
    )
    if (!ok) return
    deleteMut.mutate(initial.id, { onSuccess: () => onClose() })
  }

  const faqTitle = isEditing ? 'Edit FAQ' : 'New FAQ'
  const topicTitle = isEditing ? 'Edit briefing topic' : 'New briefing topic'

  return (
    <ModalShell
      title={faqMode ? faqTitle : topicTitle}
      onClose={onClose}
      onSubmit={handleSubmit}
      size="2xl"
      footer={
        <>
          {isEditing && (
            <DangerButton onClick={handleDelete} disabled={saving || deleting}>
              {deleting ? (faqMode ? 'Removing…' : 'Deactivating…') : (faqMode ? 'Remove' : 'Deactivate')}
            </DangerButton>
          )}
          <CancelButton onClick={onClose} disabled={saving} />
          <PrimaryButton disabled={!isValid || saving}>
            {saving ? 'Saving…' : isEditing ? 'Save changes' : (faqMode ? 'Add FAQ' : 'Add topic')}
          </PrimaryButton>
        </>
      }
      body={
        <div className="space-y-4">
          <Field label={faqMode ? 'Question' : 'Title'} error={fieldErrors.title}>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              autoFocus
              required
              className={inputClass(!!fieldErrors.title)}
              placeholder={faqMode ? 'e.g. Where can I park?' : 'e.g. Platform safety briefing'}
            />
          </Field>

          {faqMode ? (
            <Field label="Applies to" error={fieldErrors.scope} hint="'all' for every event, or an event type name (e.g. Santa Specials).">
              <input
                type="text"
                value={faqScope}
                onChange={(e) => setFaqScope(e.target.value)}
                maxLength={60}
                required
                className={inputClass(!!fieldErrors.scope)}
                placeholder="all"
              />
            </Field>
          ) : (
            <div className="grid grid-cols-[2fr_1fr] gap-3">
              <Field label="Category" error={fieldErrors.category} hint="Used to group the list.">
                <input
                  type="text"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  maxLength={80}
                  required
                  className={inputClass(!!fieldErrors.category)}
                  placeholder="Safety, Welcome, Event-specific…"
                />
              </Field>
              <Field label="Duration (min)" error={fieldErrors.duration}>
                <input
                  type="number"
                  min={0}
                  max={240}
                  step={1}
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  required
                  className={inputClass(!!fieldErrors.duration)}
                />
              </Field>
            </div>
          )}

          {!faqMode && (
            <Field
              label="Required for roles (optional)"
              hint="Comma-separated. Leave blank to apply to everyone."
            >
              <input
                type="text"
                value={rolesText}
                onChange={(e) => setRolesText(e.target.value)}
                className={inputClass(false)}
                placeholder="e.g. Steward, Guard, Platform Foreman"
              />
            </Field>
          )}

          <Field
            label={faqMode ? 'Answer (markdown)' : 'Body (markdown)'}
            error={fieldErrors.body}
            hint={faqMode ? 'Tokens {{ticketPrices}} {{departures}} {{dates}} {{eventName}} {{location}} {{runTime}} are filled with live event data when generated.' : undefined}
          >
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={12}
              required
              className={`${inputClass(!!fieldErrors.body)} font-mono text-xs leading-relaxed resize-y`}
              placeholder={faqMode
                ? 'Car parking is available near Pickering and Grosmont…'
                : '## Key points\n\n- Anti-slip strips on platform edges\n- Stand back from yellow line at all times\n'}
            />
          </Field>

          {isEditing && !faqMode && (
            <div className="text-xs text-ink-soft font-mono">
              Current version: v{initial?.version}. Saving creates v{(initial?.version ?? 0) + 1}; per-event acks from prior versions surface as stale.
            </div>
          )}

          {serverError && (
            <ServerError title="Could not save" error={serverError} />
          )}
        </div>
      }
    />
  )
}
